import React, { useEffect, useRef, useState } from 'react'
import { Container } from './Container'

// Feature transformations data
const transformations = [
  {
    id: 'evidence-gathering',
    title: 'Evidence Gathering',
    challenge: 'Manual collection and processing of audit evidence',
    solution: 'Automated extraction through seamless system integration via APIs and scheduled jobs',
    impact: 'Reduce evidence collection time by 85% while eliminating human error',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
      </svg>
    )
  },
  {
    id: 'user-termination',
    title: 'User Termination',
    challenge: 'Manual tracking and removal of terminated user access',
    solution: 'Fully automated termination workflows with complete audit trail',
    impact: 'Eliminate security vulnerabilities from delayed access termination',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M22 10.5h-6m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM4 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 10.374 21c-2.331 0-4.512-.645-6.374-1.766Z" />
      </svg>
    )
  },
  {
    id: 'documentation',
    title: 'Workpaper Documentation',
    challenge: 'Time-consuming manual documentation of audit procedures',
    solution: 'AI-generated documentation from simple inputs with consistent formatting',
    impact: 'Reduce documentation efforts by 70% while maintaining quality',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
      </svg>
    )
  },
  {
    id: 'compliance',
    title: 'Compliance Monitoring',
    challenge: 'Periodic compliance evaluations taking weeks to months',
    solution: 'Continuous real-time compliance monitoring with immediate alerts',
    impact: 'Transform reactive compliance to proactive risk management',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
    )
  },
  {
    id: 'access-requests',
    title: 'Access Request Management',
    challenge: 'Missing approval evidence and inconsistent request tracking',
    solution: 'Fully integrated approval workflow with complete audit trail',
    impact: 'Ensure 100% traceability of all access grants and changes',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
      </svg>
    )
  },
  {
    id: 'user-access-review',
    title: 'User Access Review',
    challenge: 'Manual, error-prone review with limited segregation of duties checks',
    solution: 'Automated reviews with guided workflows, SoD verification, and real-time tracking',
    impact: 'Increase review accuracy while reducing time commitment by 60%',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
      </svg>
    )
  },
  {
    id: 'risk-control-matrix',
    title: 'Risk & Control Matrix',
    challenge: 'Time-intensive manual creation of risk control matrices',
    solution: 'AI-powered risk mapping completed in minutes with downloadable RCM',
    impact: 'Reduce RCM development time from weeks to minutes',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
      </svg>
    )
  }
];

export function WorkflowTransformation() {
  const [visibleItems, setVisibleItems] = useState({});
  const [activeItem, setActiveItem] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState("evidence-gathering");
  const [mobileView, setMobileView] = useState('challenge'); // 'challenge' or 'solution'
  const [currentWarningIndex, setCurrentWarningIndex] = useState(0);
  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  
  // Update warnings array with relevant content
  const warnings = [
    {
      title: "You enjoy spending weeks collecting audit evidence",
      description: "Our automated evidence collection would rob you of those cherished late nights manually gathering documents. You'd miss all the paper cuts.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
        </svg>
      )
    },
    {
      title: "You find comfort in compliance uncertainty",
      description: "Our real-time monitoring would eliminate the thrill of discovering compliance violations months after they happen. Where's the suspense in that?",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
      )
    },
    {
      title: "You've mastered the art of last-minute user terminations",
      description: "Our automated workflows would end your adrenaline rush of racing to remove access for terminated employees before they cause problems. Boring!",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M22 10.5h-6m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM4 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 10.374 21c-2.331 0-4.512-.645-6.374-1.766Z" />
        </svg>
      )
    },
    {
      title: "You believe documentation should be an artistic expression",
      description: "Our AI-generated documentation with consistent formatting would crush your creative spirit of crafting uniquely styled audit workpapers every time.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z" />
        </svg>
      )
    },
    {
      title: "You prefer manual user access reviews",
      description: "Our automated review system would deprive you of sending hundreds of emails and following up endlessly with managers who never respond on time.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
        </svg>
      )
    },
    {
      title: "You enjoy the hunt for missing access approvals",
      description: "Our integrated approval workflow would eliminate your investigative work tracking down who approved what and when. No more detective stories to tell!",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
        </svg>
      )
    },
    {
      title: "You think risk matrices should take weeks to create",
      description: "Our AI-powered risk mapping would rob you of the joy of spending endless hours manually creating and updating your risk control matrices.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
        </svg>
      )
    }
  ];

  // Remove touch handling - we'll use scrolling instead
  const scrollRef = useRef(null);

  // Set up intersection observer to trigger animations on scroll
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisibleItems(prev => ({
            ...prev,
            [entry.target.dataset.id]: true
          }));
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    
    itemRefs.current.forEach(item => {
      if (item) {
        observer.observe(item);
      }
    });

    return () => {
      itemRefs.current.forEach(item => {
        if (item) {
          observer.unobserve(item);
        }
      });
    };
  }, []);

  const selectedTransformation = transformations.find(t => t.id === selectedItemId);

  return (
    <section 
      ref={containerRef} 
      className="py-16 sm:py-24 bg-gradient-to-b from-slate-50 to-white overflow-hidden"
    >
      {/* Background elements */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-96 left-1/2 transform -translate-x-1/2 w-[1000px] h-[1000px] rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 opacity-60 blur-3xl"></div>
        <div className="absolute top-10 right-0 w-96 h-96 bg-gradient-to-bl from-blue-100/20 to-indigo-100/20 rounded-full opacity-60 blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-72 h-72 bg-gradient-to-tr from-blue-100/30 to-indigo-100/30 rounded-full opacity-60 blur-3xl"></div>
      </div>
      
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 md:px-8 lg:px-8">
        {/* DESKTOP HEADER - Visible ONLY on desktop/tablet */}
        <div className="hidden sm:block text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <span className="inline-flex items-center rounded-full px-4 py-1 text-xs font-medium bg-gradient-to-r from-blue-600/10 to-indigo-600/10 text-blue-600 ring-1 ring-inset ring-blue-600/20 mb-3 sm:mb-4">
            Enterprise Transformation
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-gray-900 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600">
            Revolutionize Your Audit & Compliance Process
          </h2>
          <p className="mt-3 sm:mt-4 text-base sm:text-lg text-gray-600">
            Our platform transforms traditional audit and compliance workflows into streamlined, 
            automated processes that deliver unprecedented efficiency and insight.
          </p>
        </div>

        {/* MOBILE HEADER - "DO NOT USE AUDITAI if..." with horizontally scrollable cards */}
        <div className="sm:hidden mb-8">
          <div className="text-center mb-6">
            {/* Refined professional header with subtle sarcasm */}
            <div className="relative mb-5 mx-auto max-w-[95%]">
              <div className="absolute -inset-[2px] bg-gradient-to-r from-indigo-500/20 to-blue-500/20 rounded-xl blur-sm"></div>
              <div className="relative px-5 py-4 bg-white rounded-xl border border-indigo-200 shadow-sm">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-t-xl"></div>
                
                {/* Professional label */}
                <div className="flex items-center justify-center mb-2.5">
                  <span className="bg-indigo-50 text-indigo-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full border border-indigo-100">Official Notice</span>
                </div>
                
                {/* Bold heading - fix centering issues */}
                <div className="flex justify-center mb-1">
                  <span className="text-2xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-indigo-700 via-blue-700 to-indigo-700 whitespace-nowrap">WARNING: AUDIT AI</span>
                </div>
                
                {/* Subtitle with emphasized IF - improved alignment */}
                <div className="flex items-baseline justify-center mb-1">
                  <span className="text-lg font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-600 uppercase mr-1">WILL</span>
                  <p className="text-sm text-gray-600 font-medium">eliminate these beloved audit traditions</p>
                </div>
                
                {/* Elegant swipe indicator */}
                <div className="flex items-center justify-center gap-2 mt-3">
                  <div className="h-[1px] w-5 bg-gradient-to-r from-transparent to-indigo-300"></div>
                  <span className="text-xs text-indigo-500 font-medium italic">Swipe to explore</span>
                  <div className="h-[1px] w-5 bg-gradient-to-l from-transparent to-indigo-300"></div>
                </div>
                
                {/* Simple corner accent */}
                <div className="absolute top-3 right-3">
                  <svg className="w-6 h-6 text-indigo-400 opacity-70" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 17.75L5.82802 20.995L7.00702 14.122L2.00702 9.25495L8.90702 8.25495L12 2.00195L15.093 8.25495L21.993 9.25495L16.993 14.122L18.172 20.995L12 17.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          {/* Horizontally scrollable cards with premium design - Mobile View */}
          <div 
            ref={scrollRef}
            className="flex overflow-x-auto pb-6 -mx-4 px-4 space-x-4 snap-x snap-mandatory hide-scrollbar"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {warnings.map((warning, index) => (
              <div 
                key={index}
                className="snap-center flex-shrink-0 w-[280px] bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 overflow-hidden"
                style={{ aspectRatio: '1/1.3' }}
                onTouchStart={() => setCurrentWarningIndex(index)}
              >
                {/* Premium top accent */}
                <div className="h-1.5 w-full bg-gradient-to-r from-indigo-500 to-blue-500"></div>
                
                <div className="p-5 flex flex-col h-full">
                  {/* Card header with icon */}
                  <div className="flex items-start mb-4">
                    <div className="flex-shrink-0 mr-3.5">
                      <div className={`w-11 h-11 rounded-lg flex items-center justify-center transition-all duration-300 ${
                        index % 7 === 0 ? "text-indigo-600 bg-indigo-50 border border-indigo-100" :
                        index % 7 === 1 ? "text-blue-600 bg-blue-50 border border-blue-100" :
                        index % 7 === 2 ? "text-purple-600 bg-purple-50 border border-purple-100" :
                        index % 7 === 3 ? "text-emerald-600 bg-emerald-50 border border-emerald-100" :
                        index % 7 === 4 ? "text-violet-600 bg-violet-50 border border-violet-100" :
                        index % 7 === 5 ? "text-blue-600 bg-blue-50 border border-blue-100" :
                        "text-indigo-600 bg-indigo-50 border border-indigo-100"
                      }`}>
                        {warning.icon}
                      </div>
                    </div>
                    <h3 className="text-base font-bold text-gray-900 leading-tight">{warning.title}</h3>
                  </div>
                  
                  {/* Card description */}
                  <p className="text-gray-600 text-sm leading-relaxed flex-grow">{warning.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Refined scroll indicator dots */}
          <div className="flex justify-center space-x-1.5 mt-3 items-center">
            {warnings.map((_, index) => (
              <div 
                key={index} 
                className={`rounded-full transition-all duration-300 ${
                  index === currentWarningIndex 
                    ? 'h-1.5 w-8 bg-gradient-to-r from-indigo-500 to-blue-500' 
                    : 'h-1.5 w-1.5 bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Premium Enterprise Design - Tab-style selector with detailed view */}
        <div className="hidden sm:block mt-16">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden">
            {/* Top tabs - Visible only on desktop */}
            <div className="border-b border-slate-200 overflow-x-auto">
              <div className="flex py-4 px-4 space-x-2 whitespace-nowrap">
                {transformations.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setSelectedItemId(item.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      selectedItemId === item.id
                        ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {item.title}
                  </button>
                ))}
              </div>
            </div>

            {/* Content area */}
            <div className="grid grid-cols-1 md:grid-cols-6 min-h-[350px] sm:min-h-[400px]">
              {/* Left panel with feature description */}
              <div className="p-5 sm:p-6 md:p-8 col-span-2 bg-gradient-to-br from-slate-50 to-white flex flex-col justify-between">
                <div>
                  <div className="mb-5 sm:mb-6 flex gap-3 sm:gap-4 items-center">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 sm:h-14 sm:w-14 rounded-xl text-white shadow-lg bg-gradient-to-r from-blue-600 to-indigo-600">
                        {selectedTransformation.icon}
                      </div>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900">{selectedTransformation.title}</h3>
                  </div>
                  <div className="space-y-5 sm:space-y-6">
                    <div>
                      <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">The Challenge</h4>
                      <p className="text-slate-700 text-sm sm:text-base">{selectedTransformation.challenge}</p>
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Our Solution</h4>
                      <p className="text-slate-700 text-sm sm:text-base">{selectedTransformation.solution}</p>
                    </div>
                    <div>
                      <h4 className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">Business Impact</h4>
                      <p className="text-blue-900 font-medium text-sm sm:text-base">{selectedTransformation.impact}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 sm:mt-8">
                  <a href="/register" className="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-800">
                    Learn more about this feature
                    <svg className="ml-1 w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z" clipRule="evenodd" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Right panel with visual representation */}
              <div className="col-span-4 p-5 sm:p-6 md:p-0 bg-gradient-to-br from-slate-50 to-white md:bg-white relative flex flex-col">
                {/* Visual representation - Desktop mockup */}
                <div className="flex-grow relative">
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-50 via-white to-white"></div>
                  
                  {/* Before & After Content */}
                  <div className="flex-1 p-5 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-8 relative">
                    {/* Subtle background decoration */}
                    <div className="absolute h-52 w-52 -bottom-24 -right-24 bg-gradient-to-tl from-blue-50 to-transparent rounded-full opacity-50 hidden lg:block"></div>
                    
                    {/* Before Column */}
                    <div className="space-y-4 relative md:pr-4">
                      {/* Add a divider line between columns on mobile */}
                      <div className="absolute bottom-0 left-[10%] right-[10%] h-px bg-gradient-to-r from-slate-200/20 via-slate-200 to-slate-200/20 md:hidden"></div>
                    
                      {/* Vertical divider on desktop */}
                      <div className="absolute top-[10%] right-0 bottom-[10%] w-px bg-gradient-to-b from-slate-200/30 via-slate-200 to-slate-200/30 hidden md:block"></div>
                      
                      <div className="flex items-center">
                        <div className="flex-shrink-0 mr-3">
                          <span className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-gradient-to-br from-red-100 to-red-50 text-red-500 text-sm font-medium ring-4 ring-red-50 shadow-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                              <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                            </svg>
                          </span>
                        </div>
                        <h4 className="text-base md:text-lg font-medium text-gray-900">Before</h4>
                      </div>
                      <div className="pl-12 md:pl-14">
                        <div className="p-4 bg-gradient-to-br from-red-50 to-red-50/70 rounded-xl border border-red-100 shadow-sm">
                          <p className="text-gray-700">{selectedTransformation.challenge}</p>
                        </div>
                      </div>
                    </div>

                    {/* After Column */}
                    <div className="space-y-4 pt-8 md:pt-0">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 mr-3">
                          <span className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-gradient-to-br from-green-100 to-emerald-50 text-emerald-600 text-sm font-medium ring-4 ring-green-50 shadow-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                              <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143z" clipRule="evenodd" />
                            </svg>
                          </span>
                        </div>
                        <h4 className="text-base md:text-lg font-medium text-gray-900">After</h4>
                      </div>
                      <div className="pl-12 md:pl-14">
                        <div className="p-4 bg-gradient-to-br from-emerald-50 to-green-50/70 rounded-xl border border-green-100 shadow-sm">
                          <p className="text-emerald-700 font-medium">{selectedTransformation.solution}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom metrics - responsive */}
                <div className="mt-auto grid grid-cols-3 gap-2 sm:gap-4 p-4 sm:p-6 border-t border-slate-100">
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl font-bold text-blue-600">85%</div>
                    <div className="text-xs text-slate-500">Time Savings</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl font-bold text-blue-600">100%</div>
                    <div className="text-xs text-slate-500">Audit Coverage</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl sm:text-2xl font-bold text-blue-600">24/7</div>
                    <div className="text-xs text-slate-500">Monitoring</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Empty mobile placeholder to take space */}
        <div className="sm:hidden h-6"></div>

        {/* Call to action button */}
        <div className="mt-12 sm:mt-16 text-center">
          {/* Mobile version with sarcastic tone */}
          <a 
            href="/register" 
            className="sm:hidden inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl shadow-blue-500/20 hover:shadow-blue-500/30 transition-all duration-200 no-underline transform hover:-translate-y-0.5"
          >
            Make Audits Suspiciously Easy
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 ml-2">
              <path fillRule="evenodd" d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z" clipRule="evenodd" />
            </svg>
          </a>
          
          {/* Desktop version unchanged */}
          <a 
            href="/register" 
            className="hidden sm:inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 border border-transparent text-base font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl shadow-blue-500/20 hover:shadow-blue-500/30 md:text-lg transition-all duration-200 no-underline transform hover:-translate-y-0.5"
          >
            Transform Your Audit Workflow
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 ml-2">
              <path fillRule="evenodd" d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z" clipRule="evenodd" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
} 